import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Sparkles, CheckCircle2, AlertTriangle, ShieldAlert,
  Trophy, FileText, Globe, GraduationCap, Briefcase, Award, Languages, Users, MapPin,
  HelpCircle, Clock, Plane, Home as HomeIcon, Map, ScrollText, Target, Heart, Lightbulb, ListChecks, MessageCircle, Lock, ShieldCheck,
} from "lucide-react";

const STATES = ["NSW","VIC","QLD","SA","WA","TAS","ACT","NT"];
const ENG = ["none","competent","proficient","superior"];
const QUAL = ["doctorate","master","bachelor","diploma","trade","other"];
const DOCS = ["Passport","Resume","Qualifications","Transcripts","Skills Assessment","English Test","Employment Letters","Payslips","Tax Documents","Partner Documents","Marriage Certificate","NAATI","Professional Year","Visa History"];

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.18em] text-[#29324A] uppercase mb-1.5 block">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}

const STEPS = [
  { key: "personal", label: "Personal" },
  { key: "status", label: "Immigration status" },
  { key: "occupation", label: "Occupation" },
  { key: "education", label: "Education" },
  { key: "employment", label: "Employment" },
  { key: "english", label: "English" },
  { key: "additional", label: "Additional points" },
  { key: "partner", label: "Partner" },
  { key: "state", label: "State nomination" },
  { key: "sponsorship", label: "Employer sponsorship" },
  { key: "documents", label: "Documents" },
];

export default function SkilledMigrationAssessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ docs: [], states_interested: [], single: false });
  const [d1, setD1] = useState(false); const [d2, setD2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const u = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleArr = (k, v) => setData((d) => {
    const cur = d[k] || []; return { ...d, [k]: cur.includes(v) ? cur.filter(x=>x!==v) : [...cur, v] };
  });

  const validateStep = (s) => {
    const err = (msg) => { toast.error(msg); return false; };
    if (s === 0) {
      if (!data.full_name?.trim()) return err("Please enter your full name.");
      if (!data.email?.trim() || !/\S+@\S+\.\S+/.test(data.email)) return err("Please enter a valid email address.");
      if (!data.phone?.trim()) return err("Please enter a contact phone number.");
      if (!data.country_citizenship?.trim()) return err("Please enter your country of citizenship.");
      if (!data.age || Number(data.age) < 16 || Number(data.age) > 65) return err("Please enter a valid age (16–65).");
      if (!data.marital) return err("Please select your marital status.");
    }
    if (s === 2) {
      if (!data.current_occupation?.trim()) return err("Please enter your current occupation.");
      if (!data.preferred_occupation?.trim()) return err("Please enter your preferred occupation.");
      if (data.total_years == null || data.total_years === "") return err("Please enter your total years of experience.");
    }
    if (s === 3) {
      if (!data.qualification) return err("Please select your highest qualification.");
    }
    if (s === 5) {
      if (!data.english_level) return err("Please select your English language level.");
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  const submit = async () => {
    if (!d1 || !d2) { toast.error("Please tick both mandatory disclaimers."); return; }
    if (!data.full_name || !data.email) { toast.error("Name and email are required."); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/skilled-migration/submit", {
        profile: data, consent: true, disclaimer_general: d1, disclaimer_no_guarantee: d2,
        consent_timestamp: new Date().toISOString(),
      });
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  if (result) return <ResultsPage result={result} profile={data} />;

  return (
    <div>
      {/* ───────── HERO with embedded WIZARD FORM ───────── */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <span aria-hidden className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full bg-[#29324A]/30 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left — copy + value props */}
            <div className="lg:col-span-5 xl:col-span-5">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
                <Sparkles className="h-3 w-3" /> Skilled Migration Pathway Assessment
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight mt-4">
                Find Your Skilled Migration Pathway to Australia.
              </h1>
              <p className="text-[15px] md:text-base text-slate-200 leading-relaxed mt-5 max-w-xl">
                Onshore or offshore — in 5 minutes get your estimated points, best-fit visa pathways (189 / 190 / 491 / 482 / 186 / 858), risk flags and a personalised action plan emailed straight to your inbox.
              </p>

              <ul className="mt-7 space-y-2.5">
                {[
                  "Personalised points across age, English, experience, qualification, partner",
                  "Best-fit visa subclass matches — Strong / Possible / Needs Improvement",
                  "Risk analysis: refusal history, Section 48, low English, skills assessment gaps",
                  "Readiness score out of 100 + step-by-step action plan",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-slate-100">
                    <CheckCircle2 className="h-4.5 w-4.5 text-[#C43A89] mt-0.5 shrink-0" /> <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11.5px] text-slate-300">
                <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#C43A89]" /> 5–7 minutes</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-[#C43A89]" /> 100% confidential</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Free forever</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#C43A89]" /> MARN 2619415 | IAA 202603837</span>
              </div>

              <div className="mt-8 hidden lg:block bg-white/5 border border-white/10 backdrop-blur p-4">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Why we built this</div>
                <p className="text-[12.5px] text-slate-200 leading-relaxed">
                  Tired of generic advice and shifting visa rules? This is the same structured analysis our MARA agents apply to paid consultations — yours, free, in one sitting.
                </p>
              </div>
            </div>

            {/* Right — wizard FORM card */}
            <div className="lg:col-span-7 xl:col-span-7" id="assessment-form">
              <div className="bg-white text-slate-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] border border-white/10 relative">
                <span aria-hidden className="absolute -top-3 left-6 bg-[#C43A89] text-white text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 shadow-md">Start here</span>

                <div className="p-6 md:p-8">
                  {/* Progress */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2 text-[10.5px] tracking-wider uppercase text-slate-500 font-bold">
                      <span data-testid="step-label">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
                      <span data-testid="step-progress">{progress}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100">
                      <div className="h-full bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89] transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                  </div>

                  <div data-testid={`step-${step}`} className="space-y-5">
                    {step === 0 && (<>
                      <h2 className="font-display text-xl text-[#29324A]">Personal Information</h2>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name *"><Input data-testid="fld-name" value={data.full_name||""} onChange={(e)=>u("full_name",e.target.value)} /></Field>
                        <Field label="Email *"><Input data-testid="fld-email" type="email" value={data.email||""} onChange={(e)=>u("email",e.target.value)} /></Field>
                        <Field label="Phone"><Input value={data.phone||""} onChange={(e)=>u("phone",e.target.value)} /></Field>
                        <Field label="Country of citizenship"><Input value={data.country_citizenship||""} onChange={(e)=>u("country_citizenship",e.target.value)} /></Field>
                        <Field label="Country of residence"><Input value={data.country_residence||""} onChange={(e)=>u("country_residence",e.target.value)} /></Field>
                        <Field label="Date of birth"><Input type="date" value={data.dob||""} onChange={(e)=>u("dob",e.target.value)} /></Field>
                        <Field label="Age"><Input type="number" min="16" max="60" value={data.age||""} onChange={(e)=>u("age",e.target.value)} /></Field>
                        <Field label="Marital status">
                          <Select value={data.marital||""} onValueChange={(v)=>{u("marital",v); u("single", v==="single");}}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{["single","married","de_facto","separated"].map(x=>(<SelectItem key={x} value={x}>{x.replace("_"," ")}</SelectItem>))}</SelectContent>
                          </Select>
                        </Field>
                        <Field label="Dependants (#)"><Input type="number" min="0" value={data.dependants||""} onChange={(e)=>u("dependants",e.target.value)} /></Field>
                        <Field label="Preferred contact">
                          <Select value={data.contact_pref||""} onValueChange={(v)=>u("contact_pref",v)}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{["email","phone","whatsapp"].map(x=>(<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                          </Select>
                        </Field>
                      </div>
                    </>)}

            {step === 1 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Current Immigration Status</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Currently in Australia?"><YesNo v={data.currently_in_australia} on={(b)=>u("currently_in_australia",b)} /></Field>
                <Field label="Current visa type"><Input value={data.current_visa||""} onChange={(e)=>u("current_visa",e.target.value)} /></Field>
                <Field label="Visa expiry"><Input type="date" value={data.visa_expiry||""} onChange={(e)=>u("visa_expiry",e.target.value)} /></Field>
                <Field label="Previous visa refusals?"><YesNo v={data.previous_refusal} on={(b)=>u("previous_refusal",b)} /></Field>
                <Field label="Previous cancellations?"><YesNo v={data.previous_cancel} on={(b)=>u("previous_cancel",b)} /></Field>
                <Field label="Section 48 issues?"><YesNo v={data.section_48} on={(b)=>u("section_48",b)} /></Field>
                <Field label="Previous EOI submitted?"><YesNo v={data.previous_eoi} on={(b)=>u("previous_eoi",b)} /></Field>
                <Field label="Previous invitation received?"><YesNo v={data.previous_invite} on={(b)=>u("previous_invite",b)} /></Field>
                <Field label="Currently on 491 or 494?"><YesNo v={data.currently_491_or_494} on={(b)=>u("currently_491_or_494",b)} /></Field>
              </div>
            </>)}

            {step === 2 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Occupation Assessment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Current occupation"><Input value={data.current_occupation||""} onChange={(e)=>u("current_occupation",e.target.value)} /></Field>
                <Field label="Preferred occupation"><Input value={data.preferred_occupation||""} onChange={(e)=>u("preferred_occupation",e.target.value)} /></Field>
                <Field label="ANZSCO code (optional)"><Input value={data.anzsco||""} onChange={(e)=>u("anzsco",e.target.value)} /></Field>
                <Field label="Total years of experience"><Input type="number" min="0" value={data.total_years||""} onChange={(e)=>u("total_years",e.target.value)} /></Field>
                <Field label="Qualification related to occupation?"><YesNo v={data.qual_related} on={(b)=>u("qual_related",b)} /></Field>
                <Field label="Current skills assessment?">
                  <Select value={data.skills_assessment_status||""} onValueChange={(v)=>u("skills_assessment_status",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["not_started","in_progress","completed"].map(x=>(<SelectItem key={x} value={x}>{x.replace("_"," ")}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
                <Field label="Assessing authority"><Input placeholder="e.g. VETASSESS, ACS" value={data.assessing_authority||""} onChange={(e)=>u("assessing_authority",e.target.value)} /></Field>
                <Field label="Assessment outcome">
                  <Select value={data.skills_assessment_outcome||""} onValueChange={(v)=>u("skills_assessment_outcome",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["positive","partial","negative","pending"].map(x=>(<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
                <Field label="Assessment expiry"><Input type="date" value={data.assessment_expiry||""} onChange={(e)=>u("assessment_expiry",e.target.value)} /></Field>
              </div>
            </>)}

            {step === 3 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Education</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Highest qualification">
                  <Select value={data.qualification||""} onValueChange={(v)=>u("qualification",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{QUAL.map(x=>(<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
                <Field label="Country of qualification"><Input value={data.qual_country||""} onChange={(e)=>u("qual_country",e.target.value)} /></Field>
                <Field label="Course name"><Input value={data.course||""} onChange={(e)=>u("course",e.target.value)} /></Field>
                <Field label="Institution"><Input value={data.institution||""} onChange={(e)=>u("institution",e.target.value)} /></Field>
                <Field label="Completion year"><Input type="number" min="1980" max="2030" value={data.completion_year||""} onChange={(e)=>u("completion_year",e.target.value)} /></Field>
                <Field label="Australian qualification?"><YesNo v={data.australian_study} on={(b)=>u("australian_study",b)} /></Field>
                <Field label="Regional study?"><YesNo v={data.regional_study} on={(b)=>u("regional_study",b)} /></Field>
                <Field label="STEM research qualification?"><YesNo v={data.stem_research} on={(b)=>u("stem_research",b)} /></Field>
                <Field label="Professional Year completed?"><YesNo v={data.professional_year} on={(b)=>u("professional_year",b)} /></Field>
              </div>
            </>)}

            {step === 4 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Employment Experience</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Overseas skilled employment (years)"><Input type="number" min="0" value={data.overseas_years||""} onChange={(e)=>u("overseas_years",e.target.value)} /></Field>
                <Field label="Australian skilled employment (years)"><Input type="number" min="0" value={data.australian_years||""} onChange={(e)=>u("australian_years",e.target.value)} /></Field>
                <Field label="Current employment status">
                  <Select value={data.employment_status||""} onValueChange={(v)=>u("employment_status",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["full_time","part_time","casual","self_employed","unemployed","student"].map(x=>(<SelectItem key={x} value={x}>{x.replace("_"," ")}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
                <Field label="Recent graduate?"><YesNo v={data.recent_graduate} on={(b)=>u("recent_graduate",b)} /></Field>
              </div>
              <Field label="Available employment evidence (tick all that apply)">
                <div className="grid sm:grid-cols-2 gap-2 mt-1">
                  {["Payslips","Tax Records","Employment Letters","Superannuation"].map(x=>(
                    <label key={x} className="flex items-center gap-2 text-[13px] text-slate-700"><Checkbox checked={(data.evidence||[]).includes(x)} onCheckedChange={()=>toggleArr("evidence", x)} /> {x}</label>
                  ))}
                </div>
              </Field>
            </>)}

            {step === 5 && (<>
              <h2 className="font-display text-xl text-[#29324A]">English Assessment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Test type">
                  <Select value={data.english_test||""} onValueChange={(v)=>u("english_test",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["IELTS","PTE","TOEFL","Cambridge","OET","Not tested"].map(x=>(<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
                <Field label="Overall band / score"><Input value={data.english_overall||""} onChange={(e)=>u("english_overall",e.target.value)} /></Field>
                <Field label="Listening"><Input value={data.english_l||""} onChange={(e)=>u("english_l",e.target.value)} /></Field>
                <Field label="Reading"><Input value={data.english_r||""} onChange={(e)=>u("english_r",e.target.value)} /></Field>
                <Field label="Writing"><Input value={data.english_w||""} onChange={(e)=>u("english_w",e.target.value)} /></Field>
                <Field label="Speaking"><Input value={data.english_s||""} onChange={(e)=>u("english_s",e.target.value)} /></Field>
                <Field label="Estimated level" hint="Your best guess — we'll calculate points accordingly.">
                  <Select value={data.english_level||""} onValueChange={(v)=>u("english_level",v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{ENG.map(x=>(<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                  </Select>
                </Field>
              </div>
            </>)}

            {step === 6 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Additional Points</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Australian study (AUSR met)?"><YesNo v={data.australian_study} on={(b)=>u("australian_study",b)} /></Field>
                <Field label="Regional study?"><YesNo v={data.regional_study} on={(b)=>u("regional_study",b)} /></Field>
                <Field label="Professional Year?"><YesNo v={data.professional_year} on={(b)=>u("professional_year",b)} /></Field>
                <Field label="NAATI CCL?"><YesNo v={data.naati} on={(b)=>u("naati",b)} /></Field>
                <Field label="State nomination available?"><YesNo v={data.state_nomination} on={(b)=>u("state_nomination",b)} /></Field>
                <Field label="Regional nomination available?"><YesNo v={data.regional_nomination} on={(b)=>u("regional_nomination",b)} /></Field>
              </div>
            </>)}

            {step === 7 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Partner Assessment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Single?"><YesNo v={data.single} on={(b)=>u("single",b)} /></Field>
                <Field label="Partner Australian citizen / PR?"><YesNo v={data.partner_aus_pr} on={(b)=>u("partner_aus_pr",b)} /></Field>
                <Field label="Partner has Competent English?"><YesNo v={data.partner_english} on={(b)=>u("partner_english",b)} /></Field>
                <Field label="Partner skilled (skills assessment)?"><YesNo v={data.partner_skilled} on={(b)=>u("partner_skilled",b)} /></Field>
                <Field label="Partner occupation"><Input value={data.partner_occupation||""} onChange={(e)=>u("partner_occupation",e.target.value)} /></Field>
                <Field label="Partner age"><Input type="number" min="18" max="60" value={data.partner_age||""} onChange={(e)=>u("partner_age",e.target.value)} /></Field>
              </div>
            </>)}

            {step === 8 && (<>
              <h2 className="font-display text-xl text-[#29324A]">State Nomination</h2>
              <Field label="States you are interested in">
                <div className="grid sm:grid-cols-4 gap-2 mt-1">
                  {STATES.map(s=>(
                    <label key={s} className="flex items-center gap-2 text-[13px] text-slate-700"><Checkbox checked={(data.states_interested||[]).includes(s)} onCheckedChange={()=>toggleArr("states_interested", s)} /> {s}</label>
                  ))}
                </div>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <Field label="Willing to move regionally?"><YesNo v={data.regional_willing} on={(b)=>u("regional_willing",b)} /></Field>
                <Field label="Job offer?"><YesNo v={data.job_offer} on={(b)=>u("job_offer",b)} /></Field>
                <Field label="Australian graduate?"><YesNo v={data.aus_graduate} on={(b)=>u("aus_graduate",b)} /></Field>
                <Field label="Currently employed in Australia?"><YesNo v={data.currently_employed_au} on={(b)=>u("currently_employed_au",b)} /></Field>
                <Field label="Family living in regional Australia?"><YesNo v={data.family_regional} on={(b)=>u("family_regional",b)} /></Field>
              </div>
            </>)}

            {step === 9 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Employer Sponsorship</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Current employer"><Input value={data.current_employer||""} onChange={(e)=>u("current_employer",e.target.value)} /></Field>
                <Field label="Employer willing to sponsor?"><YesNo v={data.employer_sponsor} on={(b)=>u("employer_sponsor",b)} /></Field>
                <Field label="Job offer?"><YesNo v={data.job_offer} on={(b)=>u("job_offer",b)} /></Field>
                <Field label="Salary (AUD)"><Input type="number" min="0" value={data.salary||""} onChange={(e)=>u("salary",e.target.value)} /></Field>
                <Field label="Location"><Input value={data.location||""} onChange={(e)=>u("location",e.target.value)} /></Field>
                <Field label="Sponsor occupation"><Input value={data.sponsor_occupation||""} onChange={(e)=>u("sponsor_occupation",e.target.value)} /></Field>
                <Field label="Years with employer"><Input type="number" min="0" value={data.years_with_employer||""} onChange={(e)=>u("years_with_employer",e.target.value)} /></Field>
                <Field label="Training pathway considered?"><YesNo v={data.training_pathway} on={(b)=>u("training_pathway",b)} /></Field>
              </div>

              <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
                <Label className="text-[11px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-1.5 block">Exceptional Talent indicators (Subclass 858 screening)</Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-1">
                  {[
                    ["intl_achievements","International achievements / awards"],
                    ["major_business_success","Major business success"],
                    ["patents","Patents held"],
                    ["publications","Major publications"],
                    ["exec_leadership","Senior executive leadership"],
                    ["research_excellence","Research excellence"],
                  ].map(([k,label])=>(
                    <label key={k} className="flex items-center gap-2 text-[13px] text-slate-700"><Checkbox checked={!!data[k]} onCheckedChange={(v)=>u(k,!!v)} /> {label}</label>
                  ))}
                </div>
              </div>
            </>)}

            {step === 10 && (<>
              <h2 className="font-display text-xl text-[#29324A]">Document Readiness</h2>
              <p className="text-[13px] text-slate-600">Tick documents you currently have available (no upload required at this stage).</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {DOCS.map(d=>(
                  <label key={d} className="flex items-center gap-2 text-[13px] text-slate-700 bg-[#F8F4FB] border-l-2 border-[#C43A89] px-3 py-2"><Checkbox checked={(data.docs||[]).includes(d)} onCheckedChange={()=>toggleArr("docs",d)} /> {d}</label>
                ))}
              </div>

              {/* Mandatory disclaimers + consent */}
              <div className="mt-7 pt-6 border-t border-slate-200 space-y-3">
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 text-[12.5px] text-slate-700 leading-relaxed">
                  <strong className="text-rose-700">Mandatory acknowledgements:</strong> both required to submit.
                </div>
                <label className="flex items-start gap-3 text-[13px] text-slate-700 leading-relaxed cursor-pointer">
                  <Checkbox data-testid="disclaimer-1" checked={d1} onCheckedChange={(v)=>setD1(!!v)} className="mt-0.5" />
                  <span>I understand that this assessment provides general information only and does not guarantee eligibility, invitation, nomination, sponsorship or visa grant.</span>
                </label>
                <label className="flex items-start gap-3 text-[13px] text-slate-700 leading-relaxed cursor-pointer">
                  <Checkbox data-testid="disclaimer-2" checked={d2} onCheckedChange={(v)=>setD2(!!v)} className="mt-0.5" />
                  <span>I understand that migration requirements may change and that professional advice should be obtained before lodging any EOI, nomination or visa application.</span>
                </label>
              </div>
            </>)}

            {/* Nav buttons */}
            <div className="flex items-center justify-between pt-5 border-t border-slate-100">
              <Button type="button" variant="ghost" disabled={step===0} onClick={back} data-testid="wizard-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
              {step < STEPS.length - 1 ? (
                <Button type="button" onClick={next} data-testid="wizard-next" className="bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none px-6">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button type="button" onClick={submit} disabled={submitting || !d1 || !d2} data-testid="wizard-submit" className="bg-[#C43A89] hover:bg-[#a22b6e] text-white rounded-none px-6 btn-shimmer disabled:opacity-50">
                  {submitting ? "Generating report..." : "Generate my pathway report"} <Sparkles className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
                  </div>

                  {/* Slim disclaimer footer inside card */}
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-start gap-2 text-[10.5px] text-slate-500 leading-relaxed">
                    <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89] mt-0.5 shrink-0" />
                    <span>General information only. Not migration advice. Migration policy may change at any time — see full disclaimers below.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <IntroSection />

      <CTASection title="Speak with our Registered Migration Agents about your skilled migration strategy." />
    </div>
  );
}

function IntroSection() {
  const PAINS = [
    { icon: Lightbulb, text: "Exploring your visa options but unsure which pathway actually fits you?" },
    { icon: HelpCircle, text: "Overwhelmed by changing rules, points cut-offs and occupation lists?" },
    { icon: Clock,     text: "Tired of generic advice that doesn't reflect your real situation?" },
    { icon: Map,       text: "Not sure if you should target 189, 190, 491, 482, 186 or something else?" },
  ];

  const BENEFITS = [
    { icon: Target,     title: "Personalised Points Estimate",
      body: "We calculate your indicative points across age, English, qualification, experience, study, partner, regional and bonus categories — using the same logic the Department applies." },
    { icon: Map,        title: "Best-Fit Pathway Matches",
      body: "Discover which visas align with your profile — 189 / 190 / 491 / 482 / 186 / 494 / 858 — flagged as Strong, Possible or Needs Improvement." },
    { icon: ShieldAlert, title: "Risk & Eligibility Flags",
      body: "Identify red flags early — previous refusals, Section 48 bars, low English, weak experience evidence, missing skills assessment — before they cost you a refusal." },
    { icon: ListChecks, title: "Step-by-Step Action Plan",
      body: "Walk away with a clear, prioritised checklist tailored to your situation — what to fix, what to lodge, and the order in which to do it." },
    { icon: Trophy,     title: "Readiness Score Out of 100",
      body: "Know exactly how close you are to a competitive EOI — and what specific levers will lift your score fastest (English, regional, partner, PY, NAATI)." },
    { icon: FileText,   title: "Emailed Report — Yours to Keep",
      body: "A copy of your report lands in your inbox immediately. Share it with your partner, employer or our MARA agent — no more re-explaining your story." },
  ];

  const HELP = [
    { icon: ScrollText, title: "Strategy & Pathway Selection",
      body: "We choose the pathway that genuinely fits your goals, timeline and budget — not the loudest option on social media." },
    { icon: Briefcase,  title: "Skills Assessment & EOI Support",
      body: "Correct occupation choice, ANZSCO mapping, assessing authority selection (ACS/VETASSESS/Engineers Australia/etc.) and watertight EOI lodgement." },
    { icon: Globe,      title: "State & Regional Nomination",
      body: "We track invitation rounds across NSW / VIC / QLD / SA / WA / TAS / ACT / NT and align your profile to each state's evolving criteria." },
    { icon: Users,      title: "Employer Sponsorship & 482 / 186",
      body: "From SBS approval and nomination through to TSS/SID and Employer Nomination Scheme — for both onshore workers and offshore candidates." },
    { icon: Heart,      title: "Refusal & ART Strategy",
      body: "Previous refusal or cancellation? We assess merit, draft submissions, prepare witness evidence and represent at the Administrative Review Tribunal." },
    { icon: MessageCircle, title: "Multilingual, Confidential, Honest",
      body: "English, Bengali, Hindi, Nepali, Punjabi, Urdu and more. We tell you what's realistic — even when it's not what you hoped to hear." },
  ];

  return (
    <section className="bg-white border-b border-slate-100" data-testid="intro-section">
      {/* HOOK — emotional pain points */}
      <div className="bg-gradient-to-br from-[#F8F4FB] via-white to-[#FDF2F8] py-14 md:py-20 relative overflow-hidden">
        <div aria-hidden className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C43A89]/10 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#29324A]/10 blur-3xl" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
              <Sparkles className="h-3 w-3" /> Built for everyone exploring Australia
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#29324A] tracking-tight leading-tight mt-3">
              Stop guessing. Start with <em className="text-[#C43A89] not-italic">clarity</em>.
            </h2>
            <p className="text-[14.5px] md:text-base text-slate-600 leading-relaxed mt-4">
              Whether you&apos;re onshore on a student or 485 visa, sponsored by an employer, or sitting offshore wondering if Australia is even possible — this 5-minute assessment cuts through the noise and shows you the pathway that actually fits.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-9 max-w-3xl mx-auto">
            {PAINS.map((p, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/80 backdrop-blur border border-slate-200 hover:border-[#C43A89] transition-colors p-4">
                <p.icon className="h-5 w-5 text-[#C43A89] mt-0.5 shrink-0" />
                <p className="text-[13.5px] text-slate-700 leading-snug">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-9">
            <span className="inline-flex items-center gap-2 text-[12px] text-slate-600 font-medium">
              <Clock className="h-3.5 w-3.5 text-[#29324A]" /> 5–7 minutes ·
              <Lock className="h-3.5 w-3.5 text-[#29324A]" /> 100% confidential ·
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Free forever
            </span>
          </div>
        </div>
      </div>

      {/* WHAT YOU GET */}
      <div className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-10">
            <div className="md:col-span-5">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">What you&apos;ll receive</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">
                Six things waiting for you on the other side of this form.
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-[14.5px] text-slate-600 leading-relaxed">
                Migration is too important for guesswork. This tool gives you the same structured analysis our Registered Migration Agents apply to paid consultations — so you can walk into your next conversation already informed, with the right questions ready.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="group bg-white border border-slate-200 hover:border-[#C43A89] hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(196,58,137,0.25)] transition-all p-6 md:p-7 relative overflow-hidden">
                <span aria-hidden className="absolute top-0 left-0 h-1 w-12 bg-gradient-to-r from-[#29324A] to-[#C43A89]" />
                <b.icon className="h-7 w-7 text-[#C43A89]" />
                <h3 className="font-display text-lg text-[#29324A] mt-4 leading-snug">{b.title}</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed mt-2">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WHO THIS IS FOR */}
      <div className="py-14 md:py-18 bg-[#29324A] text-white relative overflow-hidden grain">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">Who this is for</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mt-3">
              Useful whether you&apos;re already here — or still dreaming from overseas.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: HomeIcon,    title: "Onshore students & 485 holders", body: "Subclass 500 students nearing graduation, 485 visa holders racing the clock, partners on dependent visas." },
              { icon: Briefcase,   title: "Sponsored & skilled workers",    body: "482/494 holders pursuing 186 transition, professionals weighing GSM vs. employer-sponsored options." },
              { icon: Plane,       title: "Offshore applicants",            body: "Skilled professionals overseas exploring 189/190/491 — including India, Bangladesh, Nepal, Philippines, UK and beyond." },
              { icon: Users,       title: "Families & partners",            body: "Couples planning joint EOIs, partner-of-skilled add-ons, dependents weighing study-then-PR pathways." },
            ].map((g, i) => (
              <div key={i} className="bg-white/5 border border-white/10 hover:bg-white/10 transition-colors p-5">
                <g.icon className="h-6 w-6 text-[#C43A89]" />
                <h3 className="font-display text-base mt-3 leading-snug">{g.title}</h3>
                <p className="text-[12.5px] text-slate-300 leading-relaxed mt-1.5">{g.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW MARA AGENTS HELP */}
      <div className="py-14 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start mb-10">
            <div className="md:col-span-5">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">After your report</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mt-3">
                How our MARA-registered agents take it from idea to outcome.
              </h2>
              <p className="text-[13.5px] text-slate-600 leading-relaxed mt-4">
                The assessment hands you direction. Our Registered Migration Agents (MARN 2619415 · IAA Licence No. 202603837) turn that direction into a lodged, well-evidenced application — with strategy, honesty and full compliance with the Migration Act 1958.
              </p>
              <Link to="/book-consultation" className="inline-flex items-center gap-2 mt-6 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 transition-colors btn-shimmer" data-testid="intro-book-consult">
                Book a 30-minute consultation <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <p className="text-[11px] text-slate-500 mt-3">AUD $110 consultation fee · credited toward engagement.</p>
            </div>
            <div className="md:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {HELP.map((h, i) => (
                  <div key={i} className="bg-white border border-slate-200 hover:border-[#C43A89] hover:shadow-[0_14px_30px_-12px_rgba(89,58,139,0.2)] transition-all p-5">
                    <h.icon className="h-5 w-5 text-[#29324A]" />
                    <h4 className="font-display text-[15px] text-[#29324A] mt-3 leading-snug">{h.title}</h4>
                    <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{h.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DISCLAIMERS */}
      <div className="py-12 md:py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-7">
            <span className="text-[10px] font-bold tracking-[0.25em] text-slate-500 uppercase">Before you begin — please read</span>
            <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight mt-2">Honest disclaimers we want you to see first.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "General information only",
                body: "This tool provides general guidance based on the information you supply. It is NOT migration advice and does not create a client–agent relationship." },
              { title: "No guarantee of outcome",
                body: "Results do not guarantee eligibility, invitation, nomination, sponsorship or visa grant. Final assessment is made solely by the Department of Home Affairs." },
              { title: "Policy may change",
                body: "Visa subclasses, points cut-offs, occupation lists, state nomination criteria and invitation rounds change frequently. Results reflect settings on the date of assessment." },
              { title: "Your data is protected",
                body: "Submissions are stored securely and shared only with our Registered Migration Agent for follow-up. We never sell your data. See our Privacy Policy." },
            ].map((d, i) => (
              <div key={i} className="flex items-start gap-3 bg-rose-50/60 border-l-4 border-rose-400 p-4">
                <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold text-[13px] text-rose-900">{d.title}</div>
                  <p className="text-[12.5px] text-slate-700 leading-relaxed mt-1">{d.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-9">
            <button type="button" onClick={() => { const el = document.getElementById("assessment-form"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="inline-flex items-center gap-2 bg-[#29324A] hover:bg-[#1f2638] text-white text-xs font-bold tracking-wider uppercase px-7 py-4 transition-colors btn-shimmer" data-testid="intro-start-assessment">
              I understand — Start my assessment <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <p className="text-[11px] text-slate-500 mt-3">11 short steps · 5–7 minutes · instant personalised report</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function YesNo({ v, on }) {
  return (
    <div className="flex gap-2">
      {[{l:"Yes",b:true},{l:"No",b:false}].map(o=>(
        <button key={o.l} type="button" onClick={()=>on(o.b)}
          className={`flex-1 px-3 py-2 text-[12px] font-bold tracking-wider uppercase border transition-colors ${v===o.b ? "bg-[#29324A] border-[#29324A] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#C43A89]"}`}
        >{o.l}</button>
      ))}
    </div>
  );
}

const BAND_TONE = {
  emerald: { card: "bg-emerald-50 border-emerald-200 text-emerald-900", bar: "bg-emerald-600" },
  amber:   { card: "bg-amber-50 border-amber-200 text-amber-900",     bar: "bg-amber-600" },
  orange:  { card: "bg-orange-50 border-orange-200 text-orange-900",  bar: "bg-orange-600" },
  rose:    { card: "bg-rose-50 border-rose-200 text-rose-900",        bar: "bg-rose-600" },
};

function ResultsPage({ result, profile }) {
  const { points, pathways, alternative, exceptional, risks, action_plan, readiness, created_at } = result;
  const bandColour = readiness.score >= 85 ? "emerald" : readiness.score >= 65 ? "amber" : readiness.score >= 45 ? "orange" : "rose";
  const tone = BAND_TONE[bandColour];
  return (
    <div>
      <PageHeader eyebrow="Skilled Migration Pathway Assessment" title="Your Skilled Migration Pathway Report." subtitle={`Generated ${new Date(created_at).toLocaleString("en-AU")} — based on settings available on the date of assessment.`} image="/assets/skilled.jpg" />

      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-7">
          {/* Warning banner */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-5 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-[13px] text-slate-700 leading-relaxed">
              <strong className="text-amber-900">Important Disclaimer:</strong> This result has been generated automatically using information you provided. We do not guarantee the accuracy, completeness or suitability of these results for any individual circumstances. This is not migration advice and should not be the sole basis for any migration decision. A review by a Registered Migration Agent is strongly recommended before taking further action.
            </div>
          </div>

          {/* Section 1 — Points & Readiness */}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-[#29324A] grain text-white p-7 relative">
              <span aria-hidden className="absolute top-0 left-0 h-1 w-16 bg-[#C43A89]" />
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Estimated points</div>
              <div data-testid="result-points" className="font-display text-5xl md:text-6xl">{points.total}</div>
              <div className="mt-5 text-[12.5px] space-y-1 text-slate-200">
                {Object.entries(points).filter(([k])=>k!=="total").map(([k,v])=>(<div key={k} className="flex justify-between border-b border-white/10 py-1"><span className="capitalize">{k.replace("_"," ")}</span><span className="text-white">{v}</span></div>))}
              </div>
            </div>
            <div className={`${tone.card} border p-7 flex flex-col justify-between`}>
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-2">Readiness score</div>
                <div data-testid="result-readiness" className="font-display text-5xl md:text-6xl">{readiness.score}<span className="text-2xl text-slate-500">/100</span></div>
                <div className="mt-3 text-sm">{readiness.band}</div>
                <div className="mt-4 h-2 w-full bg-white border border-slate-200"><div className={`h-full ${tone.bar}`} style={{width:`${readiness.score}%`}} /></div>
              </div>
              <Link to="/book-consultation" data-testid="result-book" className="mt-5 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase py-3 transition-colors btn-shimmer">Book consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>

          {/* Section 2 — Pathways */}
          <SectionBox title="Best Pathway Matches" testid="result-pathways">
            <div className="grid sm:grid-cols-2 gap-3">
              {pathways.map((p,i)=>(<PathBadge key={i} name={p.name} match={p.match} />))}
            </div>
          </SectionBox>

          {/* Section 3 — Occupation */}
          <SectionBox title="Occupation assessment summary" testid="result-occupation">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Row k="Suggested occupation" v={profile.preferred_occupation || profile.current_occupation || "—"} />
              <Row k="ANZSCO" v={profile.anzsco || "—"} />
              <Row k="Assessing authority" v={profile.assessing_authority || "—"} />
              <Row k="Assessment status" v={profile.skills_assessment_outcome || profile.skills_assessment_status || "—"} />
            </div>
          </SectionBox>

          {/* Section 4 — State */}
          {(profile.states_interested||[]).length > 0 && (
            <SectionBox title="State nomination potential" testid="result-state">
              <div className="flex flex-wrap gap-2">{profile.states_interested.map(s=>(<span key={s} className="text-[12px] bg-[#F8F4FB] border border-[#29324A]/15 text-[#29324A] px-2.5 py-1">{s}</span>))}</div>
              <p className="text-[12.5px] text-slate-600 mt-3">State nomination criteria change frequently — we monitor invitation rounds across states.</p>
            </SectionBox>
          )}

          {/* Section 5 — Employer sponsorship */}
          {profile.employer_sponsor && (
            <SectionBox title="Employer sponsorship potential" testid="result-sponsorship">
              <ul className="text-sm space-y-1.5">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> 482 — depending on stream alignment and AMSR.</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> 186 — strong if you have 2+ years with sponsor in the nominated occupation.</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> 494 — viable where the role is in a designated regional area.</li>
              </ul>
            </SectionBox>
          )}

          {/* Section 6 — Alternative */}
          {alternative.length > 0 && (
            <SectionBox title="Alternative pathways to consider" testid="result-alternative">
              {alternative.map((a,i)=>(<div key={i} className="bg-[#F8F4FB] border-l-2 border-[#C43A89] p-3 text-sm"><strong className="text-[#29324A]">{a.name}</strong><div className="text-[12.5px] text-slate-600 mt-1">{a.reason}</div></div>))}
            </SectionBox>
          )}

          {/* Section 7 — Exceptional Talent */}
          {exceptional && (
            <SectionBox title="Exceptional talent review" testid="result-exceptional">
              <div className="bg-[#29324A] text-white p-5 grain"><div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">Subclass 858</div><div className="font-display text-lg">{exceptional.name}</div><p className="text-[13px] text-slate-200 mt-1">{exceptional.note}</p></div>
            </SectionBox>
          )}

          {/* Section 8 — Risk */}
          <SectionBox title="Risk analysis" testid="result-risks">
            <ul className="space-y-2">
              {risks.map((r,i)=>(
                <li key={i} className={`flex items-start gap-2 p-3 border-l-4 ${r.level==="red"?"bg-rose-50 border-rose-500":r.level==="yellow"?"bg-amber-50 border-amber-500":"bg-emerald-50 border-emerald-500"}`}>
                  <ShieldAlert className={`h-4 w-4 mt-0.5 ${r.level==="red"?"text-rose-600":r.level==="yellow"?"text-amber-600":"text-emerald-600"}`} />
                  <span className="text-[13px] text-slate-700">{r.label}</span>
                </li>
              ))}
            </ul>
          </SectionBox>

          {/* Section 9 — Action plan */}
          <SectionBox title="Your personalised action plan" testid="result-action">
            <ol className="space-y-2">{action_plan.map((s,i)=>(<li key={i} className="flex items-start gap-3 text-sm text-slate-700"><span className="h-6 w-6 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i+1}</span> <span>{s}</span></li>))}</ol>
          </SectionBox>

          {/* Footer disclaimer */}
          <div className="bg-white border border-slate-200 p-5 text-[12px] text-slate-500 leading-relaxed">
            <p>StudyNet Pty Ltd and its representatives accept no liability for decisions made solely on the basis of this automated assessment. Users should obtain professional advice before lodging any visa-related application, nomination or Expression of Interest (EOI). Information based on settings available on the date of assessment.</p>
          </div>

          {/* Final CTA */}
          <div className="bg-[#29324A] text-white p-6 md:p-8 grain relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-1.5">Ready to explore your migration options?</div>
              <h3 className="font-display text-xl md:text-2xl leading-snug">Speak with our migration team for a personalised review.</h3>
            </div>
            <Link to="/book-consultation" className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer">Book consultation <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionBox({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-6">
      <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">{title}</div>
      {children}
    </div>
  );
}
function Row({ k, v }) { return (<div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="text-[#29324A] font-medium">{v}</span></div>); }
const MATCH_TONE = {
  strong:             { card: "bg-emerald-50 border-emerald-500", label: "text-emerald-700" },
  possible:           { card: "bg-amber-50 border-amber-500",     label: "text-amber-700" },
  needs_improvement:  { card: "bg-rose-50 border-rose-500",       label: "text-rose-700" },
};
function PathBadge({ name, match }) {
  const t = MATCH_TONE[match] || MATCH_TONE.needs_improvement;
  return (<div className={`${t.card} border-l-4 p-3`}><div className="font-display text-[15px] text-[#29324A]">{name}</div><div className={`text-[11px] font-bold tracking-wider uppercase mt-1 ${t.label}`}>{match.replace("_"," ")}</div></div>);
}
